import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import {
	CUSTOM_TASK_TYPES,
	type CustomTaskType,
	createCustomTask,
	deleteCustomTask,
	updateCustomTask,
} from "../../services/customTaskService";
import type { GetReadyCustomTaskInstance } from "../../types/task";
import { Dropdown } from "../ui/form/Dropdown";
import styles from "./CustomTaskModal.module.css";

interface CustomTaskModalProps {
	isOpen: boolean;
	onClose: () => void;
	editingTask?: GetReadyCustomTaskInstance | null;
	onSaved: () => void;
}

function readMetaTaskType(
	meta: Record<string, unknown> | undefined,
): CustomTaskType | "" {
	const raw = meta?.taskType;
	if (
		typeof raw === "string" &&
		(CUSTOM_TASK_TYPES as readonly string[]).includes(raw)
	) {
		return raw as CustomTaskType;
	}
	return "";
}

function deadlineToInputValue(deadline: string | null | undefined): string {
	if (!deadline) return "";
	const d = new Date(deadline);
	if (Number.isNaN(d.getTime())) return "";
	const yyyy = d.getUTCFullYear();
	const mm = `${d.getUTCMonth() + 1}`.padStart(2, "0");
	const dd = `${d.getUTCDate()}`.padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

function inputValueToDeadline(value: string): string | null {
	if (!value) return null;
	const d = new Date(`${value}T00:00:00.000Z`);
	if (Number.isNaN(d.getTime())) return null;
	return d.toISOString();
}

export function CustomTaskModal({
	isOpen,
	onClose,
	editingTask,
	onSaved,
}: CustomTaskModalProps) {
	const { t } = useClientTranslations("getReady");
	const { logEvent } = useAnalytics();
	const titleId = useId();
	const descId = useId();
	const deadlineId = useId();

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [taskType, setTaskType] = useState<CustomTaskType | "">("");
	const [deadline, setDeadline] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const isEditing = !!editingTask;

	useEffect(() => {
		if (!isOpen) return;
		if (editingTask) {
			setTitle(editingTask.title);
			setDescription(editingTask.description ?? "");
			setTaskType(readMetaTaskType(editingTask.meta));
			setDeadline(deadlineToInputValue(editingTask.deadline));
		} else {
			setTitle("");
			setDescription("");
			setTaskType("");
			setDeadline("");
		}
	}, [isOpen, editingTask]);

	const taskTypeOptions = CUSTOM_TASK_TYPES.map((type) => ({
		value: type,
		label: t(`customTask.types.${type}`),
	}));

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!title.trim()) {
			toast.error(t("customTask.errors.titleRequired"));
			return;
		}
		if (!taskType) {
			toast.error(t("customTask.errors.typeRequired"));
			return;
		}

		setIsSubmitting(true);
		try {
			if (isEditing && editingTask) {
				await updateCustomTask(editingTask.id, {
					title: title.trim(),
					description: description.trim() || null,
					taskType,
					deadline: inputValueToDeadline(deadline),
				});
				toast.success(t("customTask.toast.updated"));
				try {
					logEvent("custom_task_updated", undefined, { taskType });
				} catch {}
			} else {
				await createCustomTask({
					title: title.trim(),
					description: description.trim() || undefined,
					taskType,
					deadline: inputValueToDeadline(deadline),
				});
				toast.success(t("customTask.toast.created"));
				try {
					logEvent("custom_task_created", undefined, { taskType });
				} catch {}
			}
			onSaved();
			onClose();
		} catch (err) {
			toast.error(t("customTask.toast.error"));
			console.error("custom task save failed", err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = async () => {
		if (!editingTask) return;
		if (!window.confirm(t("customTask.confirmDelete"))) return;
		setIsSubmitting(true);
		try {
			await deleteCustomTask(editingTask.id);
			toast.success(t("customTask.toast.deleted"));
			try {
				logEvent("custom_task_deleted");
			} catch {}
			onSaved();
			onClose();
		} catch (err) {
			toast.error(t("customTask.toast.error"));
			console.error("custom task delete failed", err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDialogClose = () => {
		if (isSubmitting) return;
		onClose();
	};

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className={styles.dialog} onClose={handleDialogClose}>
				<Transition.Child
					as={Fragment}
					enter={styles.overlayEnter}
					enterFrom={styles.overlayEnterFrom}
					enterTo={styles.overlayEnterTo}
					leave={styles.overlayLeave}
					leaveFrom={styles.overlayLeaveFrom}
					leaveTo={styles.overlayLeaveTo}
				>
					<div className={styles.overlay} aria-hidden="true" />
				</Transition.Child>

				<div className={styles.panelWrap}>
					<Transition.Child
						as={Fragment}
						enter={styles.panelEnter}
						enterFrom={styles.panelEnterFrom}
						enterTo={styles.panelEnterTo}
						leave={styles.panelLeave}
						leaveFrom={styles.panelLeaveFrom}
						leaveTo={styles.panelLeaveTo}
					>
						<Dialog.Panel className={styles.panel}>
							<div className={styles.header}>
								<Dialog.Title className={styles.title}>
									{isEditing
										? t("customTask.editTitle")
										: t("customTask.createTitle")}
								</Dialog.Title>
								<button
									type="button"
									className={styles.closeButton}
									onClick={handleDialogClose}
									disabled={isSubmitting}
									aria-label={t("customTask.close")}
								>
									<svg
										className={styles.closeIcon}
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										aria-hidden="true"
									>
										<line x1="18" y1="6" x2="6" y2="18" />
										<line x1="6" y1="6" x2="18" y2="18" />
									</svg>
								</button>
							</div>

							<form className={styles.form} onSubmit={handleSubmit}>
								<div className={styles.field}>
									<span className={styles.label}>
										{t("customTask.fields.type")}
									</span>
									<Dropdown
										options={taskTypeOptions}
										value={taskType}
										onChange={(v) => setTaskType(v as CustomTaskType)}
										placeholder={t("customTask.fields.typePlaceholder")}
									/>
								</div>

								<label className={styles.field} htmlFor={titleId}>
									<span className={styles.label}>
										{t("customTask.fields.title")}
									</span>
									<input
										id={titleId}
										className={styles.input}
										type="text"
										value={title}
										onChange={(e) => setTitle(e.target.value)}
										placeholder={t("customTask.fields.titlePlaceholder")}
										maxLength={200}
										required
									/>
								</label>

								<label className={styles.field} htmlFor={descId}>
									<span className={styles.label}>
										{t("customTask.fields.description")}
									</span>
									<textarea
										id={descId}
										className={styles.textarea}
										value={description}
										onChange={(e) => setDescription(e.target.value)}
										placeholder={t("customTask.fields.descriptionPlaceholder")}
										maxLength={1000}
										rows={3}
									/>
								</label>

								<label className={styles.field} htmlFor={deadlineId}>
									<span className={styles.label}>
										{t("customTask.fields.deadline")}
									</span>
									<input
										id={deadlineId}
										className={styles.input}
										type="date"
										value={deadline}
										onChange={(e) => setDeadline(e.target.value)}
									/>
								</label>

								{isEditing && (
									<button
										type="button"
										className={styles.deleteButton}
										onClick={handleDelete}
										disabled={isSubmitting}
									>
										{t("customTask.actions.delete")}
									</button>
								)}

								<div className={styles.actions}>
									<button
										type="button"
										className={styles.cancelButton}
										onClick={handleDialogClose}
										disabled={isSubmitting}
									>
										{t("customTask.actions.cancel")}
									</button>
									<button
										type="submit"
										className={styles.submitButton}
										disabled={isSubmitting || !title.trim() || !taskType}
									>
										{isEditing
											? t("customTask.actions.save")
											: t("customTask.actions.add")}
									</button>
								</div>
							</form>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	);
}
